import { createEnhance } from '@halo/zustand-enhance';
import {
  MessageStatus,
  TransferStatus,
  useAppStore,
  WalletTransferStatus,
} from '@halo/chat-sdk-react';
import { useWalletStore } from '@/wallet';
import type { TransferStore } from './types';

export const useTransferStore = createEnhance<TransferStore>((_, get) => ({
  getTransactionDetails: async (params) => {
    const { chain_id, chain_type, chat_id, cli_msg_id, transfer_id } = params;
    const { client, sessionStores } = useAppStore.getState();
    const { getTransactionByHash } = useWalletStore.getState();
    const { updateMessages } = sessionStores[chat_id]?.getState() || {};

    const detail = await getTransactionByHash({
      netId: `${chain_type}_${chain_id}`,
      hash: transfer_id,
    });

    if (!detail.status) {
      return false;
    }

    const message = await client?.Api.messages.getMessageByCliMsgId(chat_id, cli_msg_id);
    const time = detail?.blockTime || message?.created_at;

    if (detail?.status === TransferStatus.Success) {
      client?.Api.messages.editLocalMessage(
        chat_id,
        cli_msg_id,
        JSON.stringify({
          ...message?.payload,
          status: detail?.status,
          time,
        })
      );
      client?.Api.messages.sendLocalMessage(chat_id, cli_msg_id);
      client?.Api.wallet.deleteWalletTransferByTransferId(transfer_id);
      updateMessages?.([{ cli_msg_id, payload: { status: detail.status, time } }]);
    } else if (detail?.status === TransferStatus.Failed) {
      client?.Api.wallet.deleteWalletTransferByTransferId(transfer_id);
      await client?.Api.messages.editLocalMessage(
        chat_id,
        cli_msg_id,
        JSON.stringify({
          ...message?.payload,
          status: detail?.status,
          time,
        })
      );
      await client?.Api.messages.canCelSendLocalMessage(chat_id, cli_msg_id);
      updateMessages?.([
        {
          cli_msg_id,
          payload: {
            status: detail.status,
            time,
          },
          status: MessageStatus.FailedSent,
        },
      ]);
    }
    return true;
  },
  loopCheckTransactionStatus: async (params) => {
    const { getTransactionDetails } = get();
    const isSuccess = await getTransactionDetails(params);
    if (isSuccess) {
      return;
    }
    const timer = setInterval(async () => {
      const isSuccess = await getTransactionDetails(params);
      if (isSuccess) {
        clearInterval(timer);
      }
    }, 8000);
  },
  getWalletTransferByTransferId: async (params) => {
    const { client } = useAppStore.getState();
    const { loopCheckTransactionStatus } = get();

    const transferPendingData = await client?.Api.wallet.getWalletTransferByTransferId(
      params.transfer_id
    );

    if (transferPendingData) {
      loopCheckTransactionStatus(transferPendingData);
    }
  },
  initWalletTransfersSend: async () => {
    const { client } = useAppStore.getState();
    const { loopCheckTransactionStatus } = get();

    const transferPendingDatas = await client?.Api.wallet.getWalletTransfersByStatus(
      WalletTransferStatus.Pending
    );

    if (transferPendingDatas) {
      transferPendingDatas.forEach((item) => {
        loopCheckTransactionStatus(item);
      });
    }
  },
}));
